import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormHelperText,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';
import { useDispatch, useSelector } from 'react-redux';
import { getUserProfile, updateUserTimezone } from 'services/apiService';
import { getAuthToken } from 'utils/authToken';
import { getIanaTimeZones } from 'utils/ianaTimeZones';
import { setAppUser, showAppAlert } from 'store/reducers/appreducer';
import { store } from 'store';

function patchAppUserFromTimezoneResponse(prev, apiRes) {
  if (!apiRes || apiRes.error) return prev;
  const d = apiRes.data !== undefined ? apiRes.data : apiRes;
  const userLike = d?.user_data || d?.user;
  if (userLike && typeof userLike === 'object') {
    return { ...prev, ...userLike };
  }
  if (d && typeof d === 'object' && d.timezone) {
    return {
      ...prev,
      timezone: d.timezone,
      timezone_source: d.timezone_source ?? 'manual'
    };
  }
  return prev;
}

function persistAppUserMerged(merged) {
  try {
    localStorage.setItem('appUser', JSON.stringify(merged));
  } catch {
    // ignore
  }
  if (sessionStorage.getItem('is_partner_session') === 'true') {
    try {
      const prev = JSON.parse(sessionStorage.getItem('user') || '{}');
      sessionStorage.setItem('user', JSON.stringify({ ...prev, ...merged }));
    } catch {
      // ignore
    }
  }
}

const TimezoneSettingsPage = () => {
  const dispatch = useDispatch();
  const appUser = useSelector((state) => state.appreducer.appUser);
  const [zones] = useState(() => getIanaTimeZones());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getAuthToken();
      if (!token) {
        if (!cancelled) setLoading(false);
        return;
      }
      const res = await getUserProfile(token);
      if (cancelled) return;
      if (!res.error && res.data) {
        dispatch(setAppUser({ appUser: res.data }));
        setValue(res.data.timezone || 'UTC');
      } else {
        const tz = store.getState().appreducer.appUser?.timezone || 'UTC';
        setValue(tz);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load profile once on mount
  }, [dispatch]);

  const zoneOptions = useMemo(() => {
    const set = new Set(zones);
    if (value && !set.has(value)) {
      return [value, ...zones].sort((a, b) => a.localeCompare(b));
    }
    return zones;
  }, [zones, value]);

  const handleSave = async () => {
    if (!value || value.length < 2 || value.length > 64) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: 'error',
          alertMessage: 'Please choose a valid IANA time zone.'
        })
      );
      return;
    }
    if (!zoneOptions.includes(value)) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: 'error',
          alertMessage: 'Time zone must be chosen from the list.'
        })
      );
      return;
    }
    const token = getAuthToken();
    if (!token) {
      dispatch(
        showAppAlert({ show: true, alertType: 'error', alertMessage: 'Not signed in.' })
      );
      return;
    }
    setSaving(true);
    const res = await updateUserTimezone(value, token);
    setSaving(false);
    if (res.error) {
      dispatch(
        showAppAlert({
          show: true,
          alertType: 'error',
          alertMessage: res.errorMessage || 'Failed to save time zone.'
        })
      );
      return;
    }
    const merged = patchAppUserFromTimezoneResponse(appUser, res);
    dispatch(setAppUser({ appUser: merged }));
    persistAppUserMerged(merged);
    dispatch(
      showAppAlert({
        show: true,
        alertType: 'success',
        alertMessage: 'Time zone saved.'
      })
    );
  };

  return (
    <Grid item xs={12} sm={12} md={8} lg={6}>
      <MainCard contentSX={{ p: 2.25 }} style={{ height: '100%' }}>
        <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
          Time zone
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Times in the app are shown using your account time zone.
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" animation="wave" height={56} />
        ) : (
          <Stack spacing={2}>
            <Autocomplete
              size="small"
              options={zoneOptions}
              value={value}
              onChange={(e, newValue) => setValue(newValue)}
              isOptionEqualToValue={(option, v) => option === v}
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((z) => z.toLowerCase().includes(q));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IANA time zone"
                  placeholder="Search regions…"
                />
              )}
            />
            {appUser?.timezone_source === 'manual' && (
              <FormHelperText>
                You chose this time zone manually. It will not be changed automatically when you sign in from another device or
                browser.
              </FormHelperText>
            )}
            <Box>
              <Button variant="contained" onClick={handleSave} disabled={saving || !value}>
                Save
              </Button>
            </Box>
          </Stack>
        )}
      </MainCard>
    </Grid>
  );
};

export default TimezoneSettingsPage;
